import type { Organization, OrganizationListItem } from '@repo/sdk/gen'
import { zOrganizationListItem } from '@repo/sdk/zod'

type OrganizationStatus = OrganizationListItem['status']

/**
 * The nine contract statuses, and the six readings an operator has.
 *
 * **Written status → reading, never the other way round, and that direction is
 * the whole point.** A map from reading to statuses can silently stop covering
 * the contract when a tenth status lands; this one cannot, because
 * `satisfies Record<OrganizationStatus, …>` makes the missing entry a compile
 * error. Nothing downstream — filter, badge, awaiting cells — can then meet a
 * status it has no reading for and fall back to printing the enum.
 *
 * Each reading is *named by one of the statuses it covers*, so the label it
 * already has in `status.*` is its label. The four rungs of the provisioning
 * ladder collapse onto `pending`, which reads "In creazione": naming each rung
 * on screen would tell the operator which Keycloak call has returned, and that
 * is not a question they have.
 */
const STATUS_READING = {
	pending: 'pending',
	identity_ready: 'pending',
	storage_ready: 'pending',
	admin_invited: 'pending',
	ready: 'ready',
	failed: 'failed',
	suspended: 'suspended',
	purging: 'purging',
	purged: 'purged'
} as const satisfies Record<OrganizationStatus, OrganizationStatus>

/**
 * The filter's choices, in the order the list moves through them.
 *
 * Derived from the map rather than written again beside it, so the two cannot
 * disagree: a reading the map stops producing leaves the filter, and one it
 * starts producing joins it, without anyone remembering to edit a second list.
 */
export const ORGANIZATION_STATUS_READINGS = [
	...new Set(Object.values(STATUS_READING))
]

export type OrganizationStatusReading =
	(typeof ORGANIZATION_STATUS_READINGS)[number]

/**
 * The statuses behind a set of readings, for the request body.
 *
 * The contract's own list is the source of the values, so a status added to it
 * reaches the filter as soon as `STATUS_READING` places it — there is no second
 * list of nine strings here to fall behind.
 */
export function statusesForReadings(
	readings: readonly OrganizationStatusReading[]
) {
	const selected = new Set<OrganizationStatusReading>(readings)
	return zOrganizationListItem.shape.status.options.filter((status) =>
		selected.has(STATUS_READING[status])
	)
}

/**
 * Whether the organization is still being created.
 *
 * This is the question the numbers columns ask: a customer mid-provisioning has
 * no members and nothing stored **by construction**, so its user count and
 * usage are blanks in waiting rather than a reading of zero or a failure to
 * load. The distinction is what decides whether somebody opens a ticket.
 */
export function isProvisioningStatus(status: OrganizationStatus) {
	return STATUS_READING[status] === 'pending'
}

/**
 * The rungs of the provisioning ladder, in the order the server climbs them.
 *
 * The first three are the three values `failedStep` can take, checked against
 * the contract rather than retyped: a step the backend adds — or renames — is a
 * compile error here instead of a rung the bar silently stops drawing.
 * `ready` is the fourth and is the destination rather than a unit of work, so
 * it is appended rather than derived.
 */
export const PROVISIONING_STEPS = [
	...([
		'identity',
		'storage',
		'admin_invite'
	] as const satisfies readonly NonNullable<Organization['failedStep']>[]),
	'ready'
] as const

export type ProvisioningStep = (typeof PROVISIONING_STEPS)[number]

export type ProvisioningStepState = 'done' | 'active' | 'failed' | 'pending'

/**
 * How many rungs each status has behind it.
 *
 * A total map again, for the same reason as `STATUS_READING`: a tenth status
 * that reached the contract without a position here would otherwise be drawn as
 * a bar with no progress at all, which is a statement — and the wrong one.
 *
 * `failed` is the one status whose position is not in the status: the rung that
 * stopped is `failedStep`, so this says `null` and {@link provisioningLadder}
 * reads it there. `suspended`, `purging` and `purged` are all four rungs behind
 * them by construction — none of the three is reachable without having been
 * `ready` first — so the bar stays full and what happened afterwards is the
 * badge's business, not the ladder's.
 */
const LADDER_REACHED = {
	pending: 0,
	identity_ready: 1,
	storage_ready: 2,
	admin_invited: 3,
	ready: 4,
	failed: null,
	suspended: 4,
	purging: 4,
	purged: 4
} as const satisfies Record<OrganizationStatus, number | null>

const FAILED_STEP_INDEX = {
	identity: 0,
	storage: 1,
	admin_invite: 2
} as const satisfies Record<NonNullable<Organization['failedStep']>, number>

/**
 * Each rung of the ladder and where this organization stands on it.
 *
 * Always all four, in the order the server climbs them: the rungs still ahead
 * are as much of the answer as the ones behind, and a bar that drew only
 * progress would hide how much is left.
 */
export function provisioningLadder(
	organization: Pick<Organization, 'status' | 'failedStep'>
) {
	return PROVISIONING_STEPS.map((step, index) => ({
		step,
		state: rungState(
			index,
			LADDER_REACHED[organization.status],
			organization.failedStep === null
				? UNRECORDED_STEP
				: FAILED_STEP_INDEX[organization.failedStep]
		)
	}))
}

/**
 * Where one rung stands, and the four readings that make a bar worth drawing.
 *
 * `done` is behind the climb, `active` is the rung being worked on right now,
 * `failed` is the rung it stopped on, `pending` is still ahead. The difference
 * between the last two is the whole point: "ferma al secondo passo" and
 * "ferma" are different pieces of news, and only the first says what to do.
 *
 * A stopped organization whose `failedStep` is null — which the contract says
 * cannot happen and the type says can — leaves every rung `pending` rather than
 * guessing at one. An empty bar beside "creazione non riuscita" says "stopped,
 * and nobody recorded where", which is true; marking rung one would not be.
 *
 * The return type is stated rather than inferred, and that is the exception to
 * this repo's rule against return annotations: TypeScript widens a returned
 * string literal to `string`, which would take the union with it and leave the
 * presentation map at the other end indexed by anything at all. Named, the four
 * readings are a total `Record` there — a fifth added here without a look
 * declared there is a compile error rather than a segment rendered unstyled.
 */
function rungState(
	index: number,
	reached: number | null,
	failedIndex: number
): ProvisioningStepState {
	if (reached === null) {
		if (index === failedIndex) {
			return 'failed'
		}
		return index < failedIndex ? 'done' : 'pending'
	}

	if (index < reached) {
		return 'done'
	}
	return index === reached ? 'active' : 'pending'
}

/**
 * The index of a step that was never recorded: before every real rung, so no
 * rung is ever `done` or `failed` on its account.
 */
const UNRECORDED_STEP = -1

/**
 * Whether retrying provisioning is a command this organization can be offered.
 *
 * The four it cannot are the four `retry-provisioning` answers with
 * `409 state_conflict`, so this map is the endpoint's own refusal restated on
 * the near side — **as a way of not offering a command that cannot work, never
 * as the thing that stops it.** The server refuses regardless, which is the
 * check that counts; hiding a button is not access control.
 *
 * The five it can include the four mid-climb ones, and deliberately: an
 * organization sitting at `storage_ready` for an hour is exactly the case the
 * retry exists for, and one that really is still moving answers
 * `already_in_progress` — which is an outcome the screen reports, not a
 * failure.
 */
export function isRetryableStatus(status: OrganizationStatus) {
	return RETRYABLE_STATUS[status]
}

const RETRYABLE_STATUS = {
	pending: true,
	identity_ready: true,
	storage_ready: true,
	admin_invited: true,
	failed: true,
	ready: false,
	suspended: false,
	purging: false,
	purged: false
} as const satisfies Record<OrganizationStatus, boolean>

/**
 * Which of the two lifecycle commands this organization can be offered.
 *
 * A third total map on the same nine statuses, for the same reason as the two
 * above: a tenth status must be a compile error here rather than an
 * organization that silently loses both commands, or keeps offering one the
 * server would refuse.
 *
 * `deactivate` covers everything up to and including a stopped creation,
 * deliberately — `suspend-organization` accepts an organization mid-climb and
 * it resumes from the same rung when it comes back, so an incident does not
 * have to wait for provisioning to finish. `purging` and `purged` have neither
 * command: both endpoints answer `409 state_conflict` once destruction has
 * begun, and a destroyed organization can never be reactivated.
 *
 * **This is what to offer, never what enforces it.** The server refuses on its
 * own, whoever calls and however they got there; a hidden button is not access
 * control.
 */
export function lifecycleCommand(status: OrganizationStatus) {
	return LIFECYCLE_COMMAND[status]
}

const LIFECYCLE_COMMAND = {
	pending: 'deactivate',
	identity_ready: 'deactivate',
	storage_ready: 'deactivate',
	admin_invited: 'deactivate',
	ready: 'deactivate',
	failed: 'deactivate',
	suspended: 'reactivate',
	purging: null,
	purged: null
} as const satisfies Record<
	OrganizationStatus,
	'deactivate' | 'reactivate' | null
>

/**
 * How far the destruction of this organization's data has got — and `null` for
 * the six statuses where it is not a thing that can be reached at all.
 *
 * **`null` is the answer for every organization that has not been deactivated,
 * and that is the point of the map.** The destruction section is not merely
 * placed away from the deactivation menu; on a live customer it does not exist.
 * `purge-organization` refuses anything that has not been through
 * `suspend-organization` with `409 state_conflict`, so the screen and the
 * server agree — the screen out of courtesy, the server as the control.
 *
 * The three stages downstream are distinct pieces of news and each gets its
 * own: `available` is a decision waiting to be taken, `running` is work to
 * watch and possibly resume, `done` is an ending with nothing left to press.
 */
export function destructionStage(status: OrganizationStatus) {
	return DESTRUCTION_STAGE[status]
}

const DESTRUCTION_STAGE = {
	pending: null,
	identity_ready: null,
	storage_ready: null,
	admin_invited: null,
	ready: null,
	failed: null,
	suspended: 'available',
	purging: 'running',
	purged: 'done'
} as const satisfies Record<
	OrganizationStatus,
	'available' | 'running' | 'done' | null
>

/**
 * Whether the first administrator's invitation is something this organization
 * has to show — and, beside it, a resend worth offering.
 *
 * **Only `ready`, and each of the other eight is excluded for its own reason
 * rather than by default.**
 *
 * The four rungs of the climb are excluded because the invitation is *being
 * issued* on one of them. `admin_invited` is the closest call of the nine: the
 * message really has gone out there. But an organization on that rung is still
 * moving, the page is still polling, and the ladder is already saying so — a
 * card reporting "pending for 0 minutes" beside a bar that is mid-climb adds a
 * second, slower narrator of the same event, and a resend pressed there races
 * the provisioning job that issued the invitation in the first place.
 *
 * `failed` is excluded because a creation that stopped has either not reached
 * the invitation rung — the answer is the retry, and the failure block already
 * says so — or stopped *on* it, where a resend cannot help either.
 *
 * `suspended`, `purging` and `purged` are excluded because nobody can walk
 * through the door the invitation opens. Activation into a deactivated
 * organization is refused by the contract itself
 * (`invite_organization_suspended`), so a resend there would send a real person
 * a real message to a locked door — the one exclusion that prevents a wrong act
 * rather than a redundant screen.
 *
 * What is left is the case the card exists for: an organization that is green,
 * finished, and possibly contains nobody at all.
 *
 * **This is what to show, never what enforces anything.** The read is
 * authorized by the operator session and the resend is refused on its own terms
 * whoever calls it; a card that is not drawn is not a control.
 */
export function showsFirstAdminInvitation(status: OrganizationStatus) {
	return FIRST_ADMIN_INVITATION[status]
}

const FIRST_ADMIN_INVITATION = {
	pending: false,
	identity_ready: false,
	storage_ready: false,
	admin_invited: false,
	ready: true,
	failed: false,
	suspended: false,
	purging: false,
	purged: false
} as const satisfies Record<OrganizationStatus, boolean>

/**
 * Whether a background job is moving this organization right now, so a screen
 * watching it has something to re-read for.
 *
 * Derived from the two maps rather than written as a third list: the four
 * provisioning rungs climb, and a destruction in `purging` walks its counters
 * up. Both are jobs whose only progress report is the next read.
 */
export function isMovingStatus(status: OrganizationStatus) {
	return (
		isProvisioningStatus(status) || DESTRUCTION_STAGE[status] === 'running'
	)
}
